import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, useEnvironment, useBi } from '@wix/yoshi-flow-editor';

import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import {
  groupsHeaderButtonClick,
  groupsNotificationShownSrc111Evid510,
} from '@wix/bi-logger-groups-data/v2';

import { LocationType } from '@wix/ambassador-socialgroups-v1-group-members-event/types';

import {
  selectEvent,
  selectEventStatus,
  selectGroupIdBySlug,
} from 'store/selectors';
import { IRootState } from 'store/types';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { EmptyState } from 'wui/EmptyState';
import { Spinner } from 'wui/Spinner';

import { getDeviceTimeZoneId, toEventImage, toFormValues } from '../helpers';
import { EventPage, EventPageHeader } from '../common/EventPage';
import { EventDrawer } from '../common/EventDrawer';
import { EventForm, IEventForm } from '../common/EventForm';

export function EditEventPage() {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const bi = useBi();

  const { events$ } = useController();
  const { params } = useCurrentStateAndParams();
  const groupId = useSelector(selectGroupIdBySlug(params.slug));
  const event = useSelector(selectEvent(params.eventSlug));

  const statuses = useSelector(selectEventStatus(event?.id || ''));

  const [isBusy, setIsBusy] = React.useState(false);

  const initialValues = toFormValues(event);

  const router = useRouter();

  const isLoading = useSelector(
    (state: IRootState) => state.events.fetch.loading,
  );
  const isUpdating = isBusy || statuses.update.loading;

  useDidUpdate(() => {
    // event has been updated
    if (!statuses.update.loading && !statuses.update.error) {
      events$.fetch(groupId);
      bi.report(
        groupsNotificationShownSrc111Evid510({
          type: 'success',
          name: 'event_updated',
          group_id: groupId,
        }),
      );
      router.go('group.events.list');
    }
  }, [statuses.update.loading]);

  if (isLoading) {
    return <EmptyState wired variant="section" title={<Spinner />} />;
  }

  if (!event) {
    // TODO: proper not found state
    return null;
  }

  if (isMobile) {
    return (
      <EventDrawer
        isOpen
        onClose={() => {
          router.go('group.events.list');
        }}
        isLoading={isUpdating}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onBusyStateChange={setIsBusy}
        title={t('groups-web.edit-event-page.header.title')}
        subtitle={t('groups-web.edit-event-page.header.subtitle')}
        actions={{
          primary: {
            label: t('groups-web.edit-event-drawer.submit'),
          },
          secondary: {
            label: t('groups-web.edit-event-drawer.cancel'),
            onClick: reportCancelClick,
          },
        }}
      />
    );
  }

  return (
    <EventPage
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onBusyStateChange={setIsBusy}
    >
      <>
        <EventPageHeader
          title={t('groups-web.edit-event-page.header.title')}
          subtitle={t('groups-web.edit-event-page.header.subtitle')}
          actions={{
            primary: {
              label: t('groups-web.edit-event-page.header.create.label'),
            },
            secondary: {
              label: t('groups-web.edit-event-page.header.cancel.label'),
              onClick: reportCancelClick,
            },
          }}
          isLoading={isUpdating}
        />

        <EventForm />
      </>
    </EventPage>
  );

  function handleSubmit(values: IEventForm) {
    bi.report(
      groupsHeaderButtonClick({
        button_name: 'publish',
        page_name: 'events_livesite',
        details: JSON.stringify({
          event_id: event?.id,
          has_photo: Boolean(values.cover),
        }),
      }),
    );

    events$.update({
      groupId,
      eventId: event?.id,
      title: values.name,
      shortDescription: values.teaser,
      location: {
        locationTbd: true,
        name: values.location,
        type: LocationType.UNKNOWN_LOCATION,
      },
      dateAndTimeSettings: {
        dateAndTimeTbd: false,
        startDate: new Date(values.startDate).toISOString(),
        endDate: new Date(values.endDate).toISOString(),
        timeZone: getDeviceTimeZoneId(),
      },
      image: toEventImage(values.cover),
    });
  }

  function reportCancelClick() {
    bi.report(
      groupsHeaderButtonClick({
        button_name: 'cancel',
        page_name: 'events_livesite',
      }),
    );
  }
}

EditEventPage.displayName = 'EditEventPage';
